import { FiCheckCircle } from "react-icons/fi"
import Header from "../../component/header/header"
import "./QuestionPreviewV2.style.scss"
import { useEffect, useRef, useState } from "react"
import { editBulkQuestions, previewBulkQuestions } from "../../api/question"
import { useNavigate, useParams } from "react-router-dom"
import toast from "react-hot-toast"
import { useNotification } from "../../Context/Notification.context"
import { listSubject } from "src/api/subject"
import QuestionCardV2 from "./QuestionCardV2"
import PreviewHeaderActions from "./PreviewHeaderActions"
import { useQuestionDataV2 } from "./useQuestionDataV2"
import {
  BulkQuestionData,
  PreviewSubjectOption,
  ProcessedQuestionContent,
  PreviewFieldErrors,
  PreviewQuestionValidationError,
  getQuestionFieldErrors,
  hasPreviewFieldErrors,
} from "../QuestionPreview/types"
import { buildSubParts } from "../../utils/subParts"
import { parseYearOrNull } from "../../utils/yearValidation"
import { QuestionStatus } from "../../api/type"
import { errorMessageShow, LoaderComponent } from "../../utils/Helper"

const resolveSelectValue = (selected: unknown) => {
  if (selected == null) return ""
  if (typeof selected === "string") return selected
  if (typeof selected === "object" && "value" in selected) {
    return String((selected as { value?: string }).value ?? "")
  }
  return ""
}

const PREVIEW_FIELD_ERROR_KEYS: Partial<
  Record<string, keyof PreviewFieldErrors>
> = {
  subject: "subject",
  chapter: "chapter",
  topic: "topic",
  grade: "grade",
  complexity: "complexity",
  question: "question",
}

const LEVEL_ERROR_KEYS: (keyof PreviewFieldErrors)[] = [
  "topic",
  "level_2",
  "level_3",
  "level_4",
]

const QuestionPreviewV2 = () => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isPageEditing, setIsPageEditing] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const originalContentRef = useRef<ProcessedQuestionContent[] | null>(null)
  const { notifications, setNotifications } = useNotification()
  const [subjects, setSubjects] = useState<PreviewSubjectOption[]>([])
  const [questionValidationErrors, setQuestionValidationErrors] = useState<
    PreviewQuestionValidationError[]
  >([])
  const [data, setData] = useState<BulkQuestionData>({
    subject: "",
    topic: "",
    complexity: "",
    grade: "",
    documents: [],
    bulk_session_id: "",
  })

  const { bulk_session_id } = useParams()
  const navigation = useNavigate()
  const { processedContent, updateContent } = useQuestionDataV2(data)

  const fetchSubjects = async () => {
    try {
      const res = await listSubject(1, "", 10000)
      const nextSubjects = res.data.data.subjects.map((item: any) => ({
        label: item.title,
        value: item.id,
        topics: [],
        chapters: [],
        level_labels: Array.isArray(item.level_labels)
          ? item.level_labels
          : undefined,
      }))
      setSubjects(nextSubjects)
    } catch (error) {
      console.error("Error fetching subjects:", error)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!bulk_session_id) return

      setLoading(true)
      originalContentRef.current = null
      setHasUnsavedChanges(false)
      try {
        const [questionsRes] = await Promise.all([
          previewBulkQuestions(bulk_session_id),
          fetchSubjects(),
        ])
        setData(questionsRes.data?.data)
      } catch (error: any) {
        console.error("Error fetching data:", error)
        errorMessageShow(
          error?.response?.data?.message ||
            "Unable to open this bulk upload preview."
        )
        if (error?.response?.status === 409 && bulk_session_id) {
          navigation(`/questions?bulk_session_id=${bulk_session_id}`)
          return
        }
        navigation("/questions")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [bulk_session_id])

  useEffect(() => {
    if (loading || processedContent.length === 0) return
    if (originalContentRef.current === null) {
      originalContentRef.current = structuredClone(processedContent)
    }
  }, [loading, processedContent])

  const notify = () =>
    toast.success("Your question has been saved successfully.", {
      icon: <FiCheckCircle color={"var(--success-600)"} size={58} />,
    })

  const handleEnterEdit = () => {
    setIsPageEditing(true)
    setQuestionValidationErrors([])
  }

  const handleExitEdit = () => {
    setIsPageEditing(false)
    setQuestionValidationErrors([])
  }

  const handleResetChanges = () => {
    if (!originalContentRef.current) return

    updateContent(structuredClone(originalContentRef.current))
    setHasUnsavedChanges(false)
    setQuestionValidationErrors([])
  }

  const handleSubmit = async (targetStatus: QuestionStatus) => {
    setSubmitting(true)
    setQuestionValidationErrors([])

    const contentWithStatus = processedContent.map((item) => ({
      ...item,
      question_status: targetStatus,
    }))

    const errors: PreviewQuestionValidationError[] = []
    contentWithStatus.forEach((item, idx) => {
      const subjectLabels = subjects.find((s) => s.value === item.subject)
        ?.level_labels
      const fieldErrors = getQuestionFieldErrors(
        item,
        targetStatus,
        subjectLabels
      )
      if (hasPreviewFieldErrors(fieldErrors)) {
        errors.push({ index: idx, errors: fieldErrors })
      }
    })

    if (errors.length > 0) {
      updateContent(contentWithStatus)
      setQuestionValidationErrors(errors)
      setIsPageEditing(true)
      setSubmitting(false)
      return
    }

    const submitData = {
      bulk_session_id,
      documents: contentWithStatus.map((item) => ({
        subject: item.subject,
        chapter_id: item.chapter_id || null,
        chapter: item.chapter || null,
        topic_id: item.topic_id || null,
        topic: item.topic || null,
        level_2_id: item.level_2_id || null,
        level_3_id: item.level_3_id || null,
        level_4_id: item.level_4_id || null,
        grade: item.grade,
        complexity: item.complexity,
        source: item.source.trim() || null,
        year: parseYearOrNull(item.year),
        marks: item.marks ? Number(item.marks) : null,
        question_status: item.question_status,
        question: item.question.replaceAll("&amp;", "&"),
        answer: item.answer || null,
        solution: item.solution || null,
        marking_scheme: item.marking_scheme || null,
        sub_parts: buildSubParts(item.subPartsText, item.sub_parts),
      })),
    }

    try {
      await editBulkQuestions(submitData)
      notify()

      const updatedNotifications = notifications.filter(
        (item) => item.bulk_session_id !== bulk_session_id
      )
      setNotifications(updatedNotifications)
      navigation("/questions")
    } catch (error) {
      console.error("Error saving questions:", error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleContentChange = (
    name: string,
    selected: unknown,
    index: number
  ) => {
    const updatedContent = processedContent.map((item, idx) => {
      if (idx === index) {
        if (name === "subject") {
          return {
            ...item,
            subject: resolveSelectValue(selected),
            chapter: "",
            chapter_id: null,
            topic: "",
            topic_id: null,
            level_2_id: null,
            level_2_name: null,
            level_3_id: null,
            level_3_name: null,
            level_4_id: null,
            level_4_name: null,
          }
        }

        if (name === "levels") {
          // selected carries the full cascading path { values, names }.
          const payload = selected as {
            values: {
              level_1: string
              level_2: string
              level_3: string
              level_4: string
            }
            names: {
              level_1?: string | null
              level_2?: string | null
              level_3?: string | null
              level_4?: string | null
            }
          }
          return {
            ...item,
            topic_id: payload.values.level_1 || null,
            topic: payload.names.level_1 ?? "",
            level_2_id: payload.values.level_2 || null,
            level_2_name: payload.names.level_2 ?? null,
            level_3_id: payload.values.level_3 || null,
            level_3_name: payload.names.level_3 ?? null,
            level_4_id: payload.values.level_4 || null,
            level_4_name: payload.names.level_4 ?? null,
          }
        }

        if (name === "complexity") {
          return {
            ...item,
            complexity: resolveSelectValue(selected),
          }
        }

        if (name === "grade") {
          return {
            ...item,
            grade: resolveSelectValue(selected),
          }
        }

        if (name === "subPartsText") {
          return {
            ...item,
            subPartsText: String(selected),
            sub_parts: buildSubParts(String(selected), item.sub_parts),
          }
        }

        return { ...item, [name]: selected }
      }
      return item
    })

    updateContent(updatedContent)
    setHasUnsavedChanges(true)

    const errorKey = PREVIEW_FIELD_ERROR_KEYS[name]
    const keysToClear =
      name === "levels" || name === "subject"
        ? LEVEL_ERROR_KEYS
        : errorKey
          ? [errorKey]
          : []
    if (keysToClear.length) {
      setQuestionValidationErrors((prev) =>
        prev
          .map((entry) => {
            if (entry.index !== index) return entry
            const nextErrors = { ...entry.errors }
            keysToClear.forEach((key) => delete nextErrors[key])
            return { index, errors: nextErrors }
          })
          .filter((entry) => hasPreviewFieldErrors(entry.errors))
      )
    }
  }

  const questionCount = processedContent.length

  return (
    <div className='content question_preview_v2'>
      <Header
        pageTitle='Question Finalization'
        buttonText='Add these Questions'
        isActive={false}
        extraButton={
          <PreviewHeaderActions
            isEditing={isPageEditing}
            submitting={submitting}
            hasUnsavedChanges={hasUnsavedChanges}
            onEdit={handleEnterEdit}
            onPreview={handleExitEdit}
            onReset={handleResetChanges}
            onPublish={() => handleSubmit("published")}
            onSaveDraft={() => handleSubmit("draft")}
          />
        }
      />

      <div
        className={`container ${isPageEditing ? "container--editing" : ""}`}
      >
        {loading ? (
          <LoaderComponent label="Loading preview…" />
        ) : questionCount === 0 ? (
          <div className='question_preview_v2__empty'>
            Processing content…
          </div>
        ) : (
          data.documents.map((_, index) => {
            const content = processedContent[index]
            if (!content) return null

            return (
              <QuestionCardV2
                key={index}
                content={content}
                subjects={subjects}
                index={index + 1}
                cardIndex={index}
                isPageEditing={isPageEditing}
                onChange={(name, selected) =>
                  handleContentChange(name, selected, index)
                }
                questionValidationErrors={questionValidationErrors}
              />
            )
          })
        )}
      </div>
    </div>
  )
}

export default QuestionPreviewV2
